// cesium-styling.ts - Manages asset styling

import {
  Cesium3DTileColorBlendMode,
  Cesium3DTileStyle,
  type Cesium3DTileset,
} from "cesium";
import type { StyleCondition } from "./cesium-types";

// Standard color map for railway IFC elements.
const ifcClassColorMap = {
  // Track components
  IfcRail: "rgba(105, 105, 105, 0.9)", // Dark Gray for rails
  IfcSleeper: "rgba(139, 69, 19, 0.8)", // Brown for sleepers/ties
  IfcFastener: "rgba(169, 169, 169, 0.9)", // Lighter Gray for fasteners
  IfcTrackElement: "rgba(192, 192, 192, 0.7)", // Silver for general track elements

  // Signaling and Electrical
  IfcSignal: "rgba(255, 0, 0, 0.9)", // Red for signals
  IfcCableSegment: "rgba(255, 165, 0, 0.8)", // Orange for cables
  IfcColumn: "rgba(0, 0, 139, 0.8)", // Dark Blue for OLE masts
  IfcBeam: "rgba(0, 0, 139, 0.8)", // Dark Blue for OLE gantries

  // Earthworks and Structures
  IfcEarthworksCut: "rgba(188, 143, 143, 0.7)", // Rosy Brown for cuts
  IfcEarthworksFill: "rgba(210, 180, 140, 0.7)", // Tan for fills
  IfcBridgePart: "rgba(112, 128, 144, 0.9)", // Slate Gray for bridges

  // Generic fallback for other elements
  // 'IfcBuildingElementProxy': 'rgba(245, 40, 145, 0.8)',
};

/**
 * Creates a set of style conditions from a map of IFC classes to colors.
 * @param colorMap - A map where keys are IFC class names and values are color strings.
 * @param ifcClassPropertyName - The name of the feature property containing the IFC class.
 * @returns An array of StyleCondition objects.
 */
function createStyleFromIfcClassMap(
  colorMap: Record<string, string>,
  ifcClassPropertyName = "Class",
): [string, string][] {
  return Object.entries(colorMap).map(([ifcClass, color]) => [
    `\${${ifcClassPropertyName}} === '${ifcClass}'`,
    `color('${color}')`,
  ]);
}

// Apply a set of style conditions to a tileset
export function applyStylesToTileset(
  viewer: any,
  tileset: Cesium3DTileset,
  overrideStyles: StyleCondition[] = [],
  applyBaseStyle = true,
) {
  if (!viewer || !tileset) {
    return;
  }

  // Generate base styles from the railway color map
  const baseStyleConditions = applyBaseStyle
    ? createStyleFromIfcClassMap(ifcClassColorMap, "className") // Assumes property is named 'Class'
    : [];

  // Convert override styles to the correct format
  const overrideStyleConditions = overrideStyles.map((style) => [
    `\${${style.property}} === '${style.value}'`,
    `color('${style.color}', ${style.opacity})`,
  ]);

  // Combine styles. Overrides come last to take precedence.
  // We also add a default condition to ensure anything not styled explicitly remains visible.
  const combinedConditions = [
    ...overrideStyleConditions,
    ...baseStyleConditions,
    ["true", "color('white')"],
  ];

  // if (combinedConditions.length === 0) {
  //   // Reset style if no conditions are provided
  //   tileset.style = new Cesium3DTileStyle({});
  //   tileset.colorBlendMode = Cesium3DTileColorBlendMode.HIGHLIGHT;

  //   viewer.scene.requestRender();
  //   return;
  // }

  const style = new Cesium3DTileStyle({
    color: {
      conditions: combinedConditions,
    },
  });
  tileset.colorBlendMode = Cesium3DTileColorBlendMode.REPLACE;

  tileset.style = style;
  viewer.scene.requestRender();
}

// Apply styles to all registered tilesets, now asynchronous
export function applyStylesToAllTilesets(
  viewer: any,
  tilesetRegistry: Map<string | number, Cesium3DTileset>,
  styleConditions: StyleCondition[],
): void {
  if (!viewer) {
    return;
  }

  try {
    for (const tileset of tilesetRegistry.values()) {
      applyStylesToTileset(viewer, tileset, styleConditions);
    }
  } catch (error) {
    console.error("Error applying styles to a tileset:", error);
    // rethrow or handle, here we rethrow so the caller's catch can handle it
    throw error;
  } finally {
    viewer.scene.requestRender();
  }
}

// Function to apply initial styles after tileset loading
export function applyInitialStyles(
  viewer: any,
  tilesetRegistry: Map<string | number, Cesium3DTileset>,
  bimAssetStyles?: StyleCondition[],
) {
  if (!bimAssetStyles || bimAssetStyles.length === 0 || !viewer) {
    return;
  }

  const readyPromises = Array.from(tilesetRegistry.values()).map(
    (tileset) => (tileset as any).readyPromise,
  );

  Promise.allSettled(readyPromises).then((results) => {
    results.forEach((result) => {
      if (result.status === "rejected") {
        console.warn("A tileset failed to load:", result.reason);
      }
    });

    // Apply styles once all tilesets have settled (either loaded or failed).
    try {
      setTimeout(() => {
        applyStylesToAllTilesets(viewer, tilesetRegistry, bimAssetStyles);
      }, 1000);
    } catch (error) {
      console.error("Error applying styles to tilesets:", error);
    }
  });
}
